import { useLocation, UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import { useMemo, useEffect, useRef, useState, useContext, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setNotification } from '../actions/appNotifications';
import history from './history';

export const useQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

export const useOnClickOutside = (ref, handler, excludeClass, altExcludeClass) => {
  useEffect(() => {
    const listener = (event) => {
      if (
        !ref.current ||
        ref.current.contains(event.target) ||
        document.getElementsByClassName(excludeClass)?.[0]?.contains(event.target) ||
        document.getElementsByClassName(altExcludeClass)?.[0]?.contains(event.target)
      ) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

export const useNotifications = (pending, error, successConfig, errorConfig) => {
  const dispatch = useDispatch();
  const previous = usePrevious({ pending, error });

  useEffect(() => {
    if (previous?.pending && !pending && !error) {
      dispatch(setNotification(successConfig));
    }
    if (previous?.pending && !pending && error) {
      dispatch(setNotification(errorConfig));
    }
  }, [pending, error]);
};

export function useBlocker(blocker, when = true) {
  const { navigator } = useContext(NavigationContext);
  useEffect(() => {
    if (!when) return;
    const unblock = history.block((tx) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock();
          tx.retry();
        }
      };
      blocker(autoUnblockingTx);
    });
    // eslint-disable-next-line consistent-return
    return unblock;
  }, [navigator, blocker, when]);
}
export function usePrompt(message, when = true) {
  const blocker = useCallback(
    (tx) => {
      if (window.confirm(message)) tx.retry();
    },
    [message]
  );
  useBlocker(blocker, when);
}

export const useOnScreen = (ref, observerOptions) => {
  const [intersectionValues, setIntersectionValues] = useState({
    isIntersecting: false,
    ratio: 0,
    width: 0,
    height: 0
  });

  useEffect(() => {
    const observable = ref.current;

    const observer = new IntersectionObserver(([entry]) => {
      setIntersectionValues((prevValues) => ({
        ...prevValues,
        isIntersecting: entry.isIntersecting,
        ratio: Math.round(entry.intersectionRatio * 100),
        width: Math.round(entry.intersectionRect.width),
        height: Math.round(entry.intersectionRect.height)
      }));
    }, observerOptions);

    observer.observe(observable);

    return () => observer.unobserve(observable);
  }, []);

  return intersectionValues;
};
